import { forEach, isEmpty, map } from 'lodash/fp';
import {
    ProductServiceOrderProcessParameters,
    ProductServiceOrderCancelParameters,
    ProductServiceOrderExtent,
    MultipleProductServiceOrderAddParameters,
} from '@mews-commander/data-access';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { lt, showSuccessAlert } from '@mews/framework';
import { notEmpty, createFetchAction, callApiInChunks } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { setServiceOrderNotes } from '../ducks/serviceOrderNotes';
import { setProductServiceOrders, updateProductServiceOrder } from '../ducks/productServiceOrders';
import { setProfiles } from '../ducks/profiles';
import { setOrderItems } from '../ducks/orderItems';
import { setProducts } from '../ducks/products';
import prefix from '../prefix';

const PRODUCT_SERVICE_ORDERS_FETCH = prefix('PRODUCT_SERVICE_ORDERS_FETCH');
const PRODUCT_SERVICE_ORDERS_ADD = prefix('PRODUCT_SERVICE_ORDERS_ADD');
const PRODUCT_SERVICE_ORDER_STATE_PROCESS = prefix('PRODUCT_SERVICE_ORDER_STATE_PROCESS');
const PRODUCT_SERVICE_ORDER_STATE_CANCEL = prefix('PRODUCT_SERVICE_ORDER_STATE_CANCEL');

interface FetchProductServiceOrdersParams {
    ownerIds?: string[];
    serviceIds?: string[];
    productServiceOrderIds?: string[];
    states?: Array<string> | null;
    extent?: ProductServiceOrderExtent;
}

const fetch =
    ({
        ownerIds,
        serviceIds,
        productServiceOrderIds,
        states,
        extent,
    }: FetchProductServiceOrdersParams): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const { productServiceOrdersComponent } = container();
        const enterpriseId = getCurrentEnterpriseId();

        const promise = productServiceOrdersComponent
            .getAll({
                enterpriseId,
                ownerIds,
                serviceIds,
                productServiceOrderIds,
                states,
                extent,
            })
            .then(({ creatorProfiles, notes, orderItems, products, productServiceOrders }: $TSFixMe) => {
                if (notEmpty(creatorProfiles)) {
                    dispatch(setProfiles(creatorProfiles));
                }

                if (notEmpty(notes)) {
                    dispatch(setServiceOrderNotes(notes));
                }
                if (notEmpty(orderItems)) {
                    dispatch(setOrderItems(orderItems));
                }

                if (notEmpty(products)) {
                    dispatch(setProducts(products));
                }
                if (notEmpty(productServiceOrders)) {
                    dispatch(setProductServiceOrders(productServiceOrders));
                }
            });
        return dispatch(createFetchAction(PRODUCT_SERVICE_ORDERS_FETCH, promise));
    };

export const fetchServiceOrderNotesByProductServiceOrderIds =
    ({ enterpriseId, productServiceOrderIds }: $TSFixMe) =>
    (dispatch: $TSFixMe, _: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { productServiceOrdersComponent } = container();

        const promise = isEmpty(productServiceOrderIds)
            ? Promise.resolve()
            : // @ts-expect-error $TSFixMeError: Argument of type '{ apiCall: any; processChunks: _.LodashMap1x1<unknown[], { productServiceOrderIds: unknown[]; enterpri...
              callApiInChunks({
                  apiCall: productServiceOrdersComponent.getAll,
                  processChunks: map((ids) => ({ productServiceOrderIds: ids, enterpriseId, extent: { notes: true } })),
              })(productServiceOrderIds).then((values) => {
                  // @ts-expect-error $TSFixMeError: No overload matches this call.
                  forEach(({ notes, productServiceOrders }) => {
                      if (notEmpty(notes)) dispatch(setServiceOrderNotes(notes));
                      if (notEmpty(productServiceOrders)) dispatch(setProductServiceOrders(productServiceOrders));
                  }, values);
              });

        return dispatch(createFetchAction(PRODUCT_SERVICE_ORDERS_FETCH, promise));
    };

export const addProductServiceOrders =
    (params: MultipleProductServiceOrderAddParameters): CommanderThunkAction<AsyncAction> =>
    async (dispatch, _, { repositories }) => {
        const promise = repositories.productServiceOrders.add(params).then(({ productServiceOrders }) => {
            if (notEmpty(productServiceOrders)) {
                dispatch(setProductServiceOrders(productServiceOrders));
            }
        });
        return dispatch(createFetchAction(PRODUCT_SERVICE_ORDERS_ADD, promise));
    };

export const processProductServiceOrder =
    (params: Pick<ProductServiceOrderProcessParameters, 'productServiceOrderId'>): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.productServiceOrders.process(params).then(({ productServiceOrder }) => {
            if (notEmpty(productServiceOrder)) {
                dispatch(updateProductServiceOrder(productServiceOrder));
            }
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });
        return dispatch(createFetchAction(PRODUCT_SERVICE_ORDER_STATE_PROCESS, promise));
    };

export const cancelProductServiceOrder =
    (params: Pick<ProductServiceOrderCancelParameters, 'productServiceOrderId'>): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.productServiceOrders.cancel(params).then(({ productServiceOrder }) => {
            if (notEmpty(productServiceOrder)) {
                dispatch(updateProductServiceOrder(productServiceOrder));
            }
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });
        return dispatch(createFetchAction(PRODUCT_SERVICE_ORDER_STATE_CANCEL, promise));
    };

export const fetchProductServiceOrders = (params: FetchProductServiceOrdersParams) => fetch(params);
